import { forwardRef, HTMLAttributes, createContext, useContext } from 'react';
import { m, Variants } from 'framer-motion';
import { clsx } from 'clsx';

// Omit HTML animation events that conflict with Framer Motion
type MotionSafeHTMLAttributes<T> = Omit<
  HTMLAttributes<T>,
  'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration' | 'onDrag' | 'onDragStart' | 'onDragEnd'
>;

// Context for sibling fade effect
interface PortfolioGridContextValue {
  isAnyHovered: boolean;
  hoveredId: string | null;
}

export const PortfolioGridContext = createContext<PortfolioGridContextValue>({
  isAnyHovered: false,
  hoveredId: null,
});

export interface PortfolioCardProps extends MotionSafeHTMLAttributes<HTMLElement> {
  id: string;
  title: string;
  description?: string;
  thumbnail: string;
  tags?: string[];
  client?: string;
  date?: string;
  href?: string;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
}

const cardVariants: Variants = {
  initial: { opacity: 1 },
  hover: { opacity: 1 },
  sibling: { opacity: 0.4 },
};

const imageVariants: Variants = {
  initial: { scale: 1 },
  hover: { scale: 1.05 },
};

const overlayVariants: Variants = {
  initial: { opacity: 0 },
  hover: { opacity: 1 },
};

export const PortfolioCard = forwardRef<HTMLElement, PortfolioCardProps>(
  (
    {
      id,
      title,
      description,
      thumbnail,
      tags = [],
      client,
      date,
      href,
      onHoverStart,
      onHoverEnd,
      className,
      ...props
    },
    ref
  ) => {
    const { isAnyHovered, hoveredId } = useContext(PortfolioGridContext);
    const isHovered = hoveredId === id;
    const isSibling = isAnyHovered && !isHovered;

    const Component = href ? m.a : m.article;
    const linkProps = href ? { href } : {};

    return (
      <Component
        ref={ref as any}
        variants={cardVariants}
        animate={isSibling ? 'sibling' : isHovered ? 'hover' : 'initial'}
        transition={{ duration: 0.3, ease: 'easeOut' }}
        onHoverStart={onHoverStart}
        onHoverEnd={onHoverEnd}
        className={clsx(
          'group block relative overflow-hidden rounded-xl bg-white cursor-pointer',
          className
        )}
        {...linkProps}
        {...props}
      >
        {/* Thumbnail */}
        <div className="relative aspect-[4/3] overflow-hidden">
          <m.img
            src={thumbnail}
            alt={title}
            variants={imageVariants}
            animate={isHovered ? 'hover' : 'initial'}
            transition={{ duration: 0.4, ease: 'easeOut' }}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />

          {/* Hover overlay */}
          <m.div
            variants={overlayVariants}
            animate={isHovered ? 'hover' : 'initial'}
            transition={{ duration: 0.3 }}
            className="absolute inset-0 bg-gradient-to-t from-text/80 via-text/20 to-transparent"
          />

          {/* Hover content */}
          <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={isHovered ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.3 }}
            className="absolute bottom-0 left-0 right-0 p-4 text-white"
          >
            {description && (
              <p className="text-sm opacity-90 line-clamp-2">{description}</p>
            )}
          </m.div>
        </div>

        {/* Card content */}
        <div className="p-4">
          <h3 className="text-lg font-semibold text-text line-clamp-1">{title}</h3>

          <div className="flex items-center gap-2 mt-2 text-sm text-text/60">
            {client && <span>{client}</span>}
            {client && date && <span>·</span>}
            {date && <span>{date}</span>}
          </div>

          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="px-2 py-0.5 text-xs bg-text/5 text-text/70 rounded-full"
                >
                  {tag}
                </span>
              ))}
              {tags.length > 3 && (
                <span className="px-2 py-0.5 text-xs text-text/50">
                  +{tags.length - 3}
                </span>
              )}
            </div>
          )}
        </div>
      </Component>
    );
  }
);

PortfolioCard.displayName = 'PortfolioCard';
